import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  RefreshCw,
  Users,
  Building2,
  Calendar,
  Download
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { getPayrollCostReport } from '../../api/reports';
import { getBranches } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PayrollCostReportPage = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');

  const now = new Date();
  const [dateRange, setDateRange] = useState({
    start: formatDateToISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDateToISO(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        start_date: dateRange.start,
        end_date: dateRange.end
      };
      if (selectedBranch) {
        filters.branch_id = selectedBranch;
      }
      const response = await getPayrollCostReport(filters);
      if (response.success) {
        setReport(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch report');
      }
    } catch (error) {
      console.error('Error fetching payroll cost report:', error);
      toast.error('Failed to load payroll cost report');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedBranch]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const loadBranches = async () => {
    try {
      const response = await getBranches();
      if (response.success) {
        setBranches(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  const handleDownload = () => {
    toast.info('Downloading report...');
    // Implement report export if needed
  };

  const summary = report?.summary || {};
  const branchBreakdown = report?.breakdown?.by_branch || [];
  const componentBreakdown = report?.breakdown?.by_component || {};

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Payroll Cost Report
                </h1>
                <p className="text-sm text-gray-500">Summary of employee compensation costs</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm"
                />
                <button onClick={fetchReport} className="p-2 border rounded-lg hover:bg-gray-50">
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner message="Calculating payroll costs..." className="py-12" />
        ) : report ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-gray-600">Employees</span>
                </div>
                <p className="text-2xl font-bold">{summary.employee_count || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-600">Total Gross</span>
                </div>
                <p className="text-xl font-bold text-green-600">
                  <BlurredValue value={formatCurrency(summary.total_gross)} />
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-gray-600">Total PAYE</span>
                </div>
                <p className="text-xl font-bold text-red-600">
                  <BlurredValue value={formatCurrency(summary.total_paye)} />
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-gray-600">Net Pay</span>
                </div>
                <p className="text-xl font-bold text-amber-600">
                  <BlurredValue value={formatCurrency(summary.total_net)} />
                </p>
              </div>
            </div>

            {/* Deduction Summary */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
              <h3 className="font-semibold mb-4">Deduction Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Employee Pension</p>
                  <p className="text-lg font-bold">
                    <BlurredValue value={formatCurrency(summary.total_pension_employee)} />
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Employer Pension</p>
                  <p className="text-lg font-bold">
                    <BlurredValue value={formatCurrency(summary.total_pension_employer)} />
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Loan Deductions</p>
                  <p className="text-lg font-bold">
                    <BlurredValue value={formatCurrency(summary.total_loan_deductions)} />
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Deductions</p>
                  <p className="text-lg font-bold text-red-600">
                    <BlurredValue
                      value={formatCurrency(
                        parseFloat(summary.total_paye || 0) +
                          parseFloat(summary.total_pension_employee || 0) +
                          parseFloat(summary.total_loan_deductions || 0)
                      )}
                    />
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly Breakdown */}
            {report.monthly_breakdown?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Monthly Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Payslips</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Gross</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">PAYE</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">
                          Pension (Emp)
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Pension (Er)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.monthly_breakdown.map((month, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">
                            {new Date(month.month).toLocaleDateString('en-US', {
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="px-4 py-3 text-center">{month.payslip_count}</td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(month.total_gross)} />
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            <BlurredValue value={formatCurrency(month.total_paye)} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(month.total_pension_employee)} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(month.total_pension_employer)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600">
                            <BlurredValue value={formatCurrency(month.total_net)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* By Employee */}
            {report.by_employee?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  By Employee
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Payslips</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Total Gross</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Total PAYE</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">
                          Total Pension
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Total Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.by_employee.map((emp, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{emp.employee_name}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {emp.employee_branch_name || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-center">{emp.payslip_count}</td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(emp.total_gross)} />
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            <BlurredValue value={formatCurrency(emp.total_paye)} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(emp.total_pension)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600">
                            <BlurredValue value={formatCurrency(emp.total_net)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
            No payroll data available for the selected period.
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollCostReportPage;
